import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LangLink } from "@/components/LangLink";
import { ArrowRight, ArrowUpRight, Truck, ShieldCheck, Globe } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ProductCard } from "@/components/ProductCard";
import { AeoAnswerBlock } from "@/components/aeo/AeoAnswerBlock";
import { AeoFaq } from "@/components/aeo/AeoFaq";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getCatalogProducts, getLandingPage } from "@/lib/api";
import { SITE_URL } from "@/lib/siteConfig";
import { useI18n } from "@/context/I18nContext";
import NotFound from "@/pages/NotFound";

const mapProduct = (p) => ({
  handle: p.handle,
  title: p.title,
  image: p.featured_image || (p.images && p.images[0]) || null,
  brand: p.vendor,
  price: p.price_min,
  badge: null,
});

export default function LandingPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const { tx, lang } = useI18n();
  const [cfg, setCfg] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [cfgLoading, setCfgLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch the page content (translated server-side for the active language).
  useEffect(() => {
    let cancelled = false;
    setCfgLoading(true);
    setNotFound(false);
    window.scrollTo({ top: 0 });
    getLandingPage(slug, lang)
      .then((d) => { if (!cancelled) setCfg(d); })
      .catch((e) => { if (!cancelled) { setCfg(null); if (e?.response?.status === 404) setNotFound(true); } })
      .finally(() => { if (!cancelled) setCfgLoading(false); });
    return () => { cancelled = true; };
  }, [slug, lang]);

  // Fetch the live Shopify product grid once we know the query.
  useEffect(() => {
    if (!cfg?.query) return;
    let cancelled = false;
    setLoading(true);
    getCatalogProducts(cfg.query)
      .then((d) => { if (!cancelled) setProducts((d.products || []).map(mapProduct)); })
      .catch(() => { if (!cancelled) setProducts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cfg]);

  if (notFound) return <NotFound />;

  if (cfgLoading || !cfg) {
    return (
      <div className="container-site py-20" data-testid="landing-loading">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="mt-4 h-5 w-1/2" />
        <div className="mt-10 grid grid-cols-2 gap-5 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (<Skeleton key={i} className="aspect-[4/5] w-full" />))}
        </div>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: cfg.h1,
    description: cfg.metaDescription,
    url: `${SITE_URL}/${slug}`,
    isPartOf: { "@type": "WebSite", name: "Boomerang.ie", url: SITE_URL },
    about: { "@type": "Brand", name: "Kentucky Horsewear" },
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: cfg.h1 },
      ],
    },
  };

  return (
    <>
      <Seo title={cfg.metaTitle} description={cfg.metaDescription} path={`/${slug}`} image={cfg.heroImage} jsonLd={jsonLd} />

      {/* HERO — navy chapter */}
      <section className="section-navy grain relative overflow-hidden" data-testid="landing-hero">
        <img src={cfg.heroImage} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--brand-navy))] via-[hsl(var(--brand-navy))]/85 to-[hsl(var(--brand-navy))]/55" />
        <div className="container-site relative grid items-center gap-8 py-16 lg:grid-cols-12 lg:py-24">
          <div className="lg:col-span-8">
            <Reveal>
              {cfg.eyebrow && <p className="eyebrow text-[hsl(var(--primary))]">{cfg.eyebrow}</p>}
              <h1 className="mt-3 font-display text-4xl leading-[1.0] text-white sm:text-6xl">{cfg.h1}</h1>
              <div className="mt-6 h-px w-24 bg-[hsl(var(--primary))]" />
              <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-white/80">{cfg.intro}</p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" data-testid="landing-shop-cta">
                  <a href="#products">{tx("Shop the Collection")} <ArrowRight className="ml-2 h-4 w-4" /></a>
                </Button>
                {cfg.shipping && (
                  <span className="inline-flex items-center gap-2 text-sm text-white/70">
                    <Truck className="h-4 w-4 text-[hsl(var(--primary))]" /> {cfg.shipping}
                  </span>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRUST ROW */}
      <section className="border-b border-foreground/10 bg-secondary/30">
        <div className="container-site grid gap-4 py-8 sm:grid-cols-3">
          {[
            { icon: ShieldCheck, t: tx("Official reseller"), d: tx("Authentic Kentucky Horsewear with full warranty.") },
            { icon: Globe, t: tx("Shipped worldwide"), d: tx("Express, tracked delivery from Ireland & EU.") },
            { icon: Truck, t: cfg.region ? `${tx("Delivery to")} ${cfg.region}` : tx("Fast dispatch"), d: cfg.shipping },
          ].map((b, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <b.icon className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--primary))]" />
              <div>
                <p className="text-sm font-semibold">{b.t}</p>
                <p className="text-sm text-muted-foreground">{b.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="products" className="container-site scroll-mt-24 py-14 lg:py-20" data-testid="landing-products">
        <div className="mb-8 flex items-end justify-between gap-4">
          <Reveal><h2 className="font-display text-3xl sm:text-4xl">{tx("Shop the edit")}</h2></Reveal>
          <LangLink to="/brand/kentucky" className="inline-flex items-center gap-1.5 text-sm font-medium uppercase tracking-[0.12em] text-foreground/70 hover:text-foreground">
            {tx("View all")} <ArrowUpRight className="h-4 w-4" />
          </LangLink>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 gap-x-5 gap-y-9 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (<div key={i}><Skeleton className="aspect-[4/5] w-full" /><Skeleton className="mt-3 h-4 w-2/3" /></div>))}
          </div>
        ) : products.length ? (
          <div className="grid grid-cols-2 gap-x-5 gap-y-9 md:grid-cols-3 lg:grid-cols-4">
            {products.map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)}
          </div>
        ) : (
          <p className="text-muted-foreground">{tx("Products are loading. Please check back shortly.")}</p>
        )}
      </section>

      {/* AEO ANSWERS — navy */}
      {cfg.answers?.length > 0 && (
        <section className="section-navy grain relative" data-testid="landing-aeo">
          <div className="container-site py-16 lg:py-20">
            <Reveal>
              <p className="eyebrow text-[hsl(var(--primary))]">{tx("Ask anything")}</p>
              <h2 className="mt-3 max-w-2xl font-display text-3xl leading-tight text-white sm:text-4xl">{tx("Quick answers")}</h2>
            </Reveal>
            <div className="mt-9 grid gap-4 lg:grid-cols-2">
              {cfg.answers.map((a, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <AeoAnswerBlock question={a.q} answer={a.a} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LONG-FORM EDITORIAL SECTIONS */}
      {cfg.sections?.length > 0 && (
        <section className="bg-background" data-testid="landing-sections">
          <div className="container-site py-16 lg:py-20">
            <div className="mx-auto grid max-w-4xl gap-12">
              {cfg.sections.map((s, i) => (
                <Reveal key={i} delay={i * 0.04}>
                  <article className="prose prose-neutral max-w-none prose-headings:font-display">
                    <h2>{s.h}</h2>
                    {s.paras.map((p, j) => <p key={j}>{p}</p>)}
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SEO CONTENT + FAQ */}
      <section className="border-t border-foreground/10 bg-secondary/30">
        <div className="container-site grid gap-12 py-16 lg:grid-cols-2 lg:py-20">
          <article className="prose prose-neutral max-w-none prose-headings:font-display">
            <h2>{cfg.h1}</h2>
            {cfg.body.map((para, i) => <p key={i}>{para}</p>)}
            <p className="not-prose mt-4 flex flex-wrap gap-2">
              {cfg.links.map((l) => (
                <LangLink key={l.to} to={l.to} className="rounded-full border border-foreground/15 bg-card px-3.5 py-1.5 text-sm font-medium no-underline hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]">{l.label}</LangLink>
              ))}
            </p>
          </article>
          <div>
            {cfg.faqs?.length > 0 && <AeoFaq faqs={cfg.faqs} title={tx("Frequently asked questions")} />}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="section-navy">
        <div className="container-site flex flex-col items-start justify-between gap-5 py-12 sm:flex-row sm:items-center">
          <h2 className="font-display text-2xl text-white sm:text-3xl">{tx("Ready to shop Kentucky Horsewear?")}</h2>
          <Button asChild size="lg"><LangLink to="/brand/kentucky">{tx("Explore the Range")} <ArrowRight className="ml-2 h-4 w-4" /></LangLink></Button>
        </div>
      </section>
    </>
  );
}
